/**
 * Single source of truth for on-site FAQ content.
 * SUPPORT_FAQS feeds the shared <FAQ> accordion (pricing + /support).
 * PRODUCT_FAQS feed the per-product FAQ sections and their FAQPage schema.
 */

export interface FaqEntry {
  question: string;
  answer: string;
}

export const SUPPORT_FAQS: FaqEntry[] = [
  {
    question: 'What is AxionQuant?',
    answer:
      'AxionQuant is a bleeding-edge financial data provider and financial data API powering applications, research, quantitative modeling, and AI/ML workflows. It provides clean, accurate financial datasets through REST API, Python SDK, JavaScript SDK, and MCP, covering 16 data groups including stocks, ETFs, forex, futures, crypto, fundamentals, economic data, and alternative data.',
  },
  {
    question: 'Is there a free tier available?',
    answer:
      'Yes, AxionQuant offers a free tier that allows you to explore the API, test endpoints, and even retrieve a complete list of over 19,000 US stocks in a single request. Check our pricing page for rate limits and feature comparisons.',
  },
  {
    question: 'How do I authenticate my requests?',
    answer:
      'You authenticate by including an API key in the header of each request. After signing up, you\'ll receive your unique API key. For the Python SDK, you can set it once via `axion.api_key = "your_key"` or use an environment variable.',
  },
  {
    question: 'What financial data can I access?',
    answer:
      'You can access: equities, ETFs, crypto, forex, futures, indices, macroeconomic data, company fundamentals (income statements, balance sheets, cash flow), SEC filings, earnings estimates, insider & institutional holdings, ESG scores, supply chain relationships, news sentiment, web traffic analytics, and more.',
  },
  {
    question: 'Can I cancel my subscription?',
    answer:
      'You can cancel your subscription at any time from your account dashboard. You\'ll continue to have access until the end of your current billing period, with no further charges.',
  },
  {
    question: 'What are the rate limits?',
    answer:
      'AxionQuant\'s metered API endpoints are limited to 60 requests per minute. These limits only apply to news, supply chain, credit, ESG, sentiment, and web traffic endpoints. All other API endpoints are unmetered.',
  },
  {
    question: 'Do you provide historical backtesting data?',
    answer:
      'Yes. AxionQuant provides historical market, fundamental, and macroeconomic data for backtesting quantitative trading strategies and financial models. This includes historical OHLCV prices for stocks, crypto, forex, and futures, along with historical EPS, revenue, market capitalization, financial metrics, and economic indicators, enabling complex, data-driven backtests.',
  },
  {
    question: 'Is there a Python SDK, and how do I install it?',
    answer:
      'Yes, we offer a fully featured Python SDK. Install it via pip: `pip install axionquant-sdk`. The SDK handles authentication, rate limiting, caching, and returns pandas DataFrames for easy analysis.',
  },
  {
    question: 'Can I access the API without Python?',
    answer:
      'Absolutely. AxionQuant provides a standard REST API that works with any programming language (JavaScript, R, Go, etc.) or tool like Postman. The SDK is just a convenient wrapper.',
  },
  {
    question: 'What kind of support do you offer?',
    answer:
      'We provide email support for all users. Paid plans include priority support and SLAs. Additionally, our documentation includes interactive examples, and we have a public Discord/community forum for general questions.',
  },
  {
    question: 'Is my data usage private?',
    answer:
      'Yes. We do not sell or share your query data with third parties. Your API keys and usage patterns are kept confidential. Please review our privacy policy for full details.',
  },
  {
    question: 'Do you offer discounted plans for students or researchers?',
    answer:
      'Yes, we offer academic discounts. Contact our support team with your academic email and a brief description of your research, and we\'ll provide a reduced rate or extended free trial.',
  },
];

export const PRODUCT_FAQS: Record<string, FaqEntry[]> = {
  stocks: [
    {
      question: 'How far back does historical stock price data go?',
      answer:
        'The Stocks API serves full daily OHLCV history for US-listed equities, adjusted for splits, so you can backtest across decades of market activity with a single endpoint call.',
    },
    {
      question: 'How do I find the right ticker symbol?',
      answer:
        'Use the find-tickers endpoint to search the equity universe by name, symbol or asset parameters. It returns matching tickers with metadata so you can resolve companies before pulling quotes or fundamentals.',
    },
    {
      question: 'Are quotes real-time or delayed?',
      answer:
        'Quotes are served at high frequency suitable for research and intraday workflows. Exact refresh cadence depends on your plan tier; see the pricing page for rate limits per plan.',
    },
    {
      question: 'Does the stock data include fundamentals too?',
      answer:
        'Price data and reference details live in the Stocks section, while statements, ratios, DCF valuation and time series live under Fundamentals. Both share the same ticker identifiers, so joining them takes one line of code.',
    },
  ],
  etfs: [
    {
      question: 'Can I see what an ETF actually holds?',
      answer:
        'Yes. The holdings endpoint returns an ETF\'s underlying positions, and companion endpoints give holding weights and sector or asset-class exposure breakdowns for any fund.',
    },
    {
      question: 'Which ETFs are covered?',
      answer:
        'The universe spans US-listed ETFs across equity, fixed income, commodity and multi-asset funds. Use the list endpoint to pull every supported fund and its metadata in one request.',
    },
    {
      question: 'Do you provide ETF price history?',
      answer:
        'Every covered fund has full historical OHLCV data alongside quotes and fund profiles, ready for performance analysis and factor research.',
    },
    {
      question: 'How do exposure breakdowns work?',
      answer:
        'The exposure endpoint aggregates a fund\'s holdings into sector, geography and asset-class views, useful for overlap analysis when combining multiple ETFs in one portfolio.',
    },
  ],
  forex: [
    {
      question: 'Which currency pairs are available?',
      answer:
        'Major, minor and emerging-market pairs are covered. Use the find-tickers endpoint to search pairs by currency codes, or the list endpoint to enumerate everything available.',
    },
    {
      question: 'How is FX quote data formatted?',
      answer:
        'Each pair is treated like any other instrument: a ticker, live quote and historical OHLCV series, so the same code that pulls stock data pulls exchange rates with no changes.',
    },
    {
      question: 'Can I use FX data for macro research?',
      answer:
        'Yes, historical exchange rates pair naturally with the economic datasets and calendar endpoints, letting you study regional market moves around macro releases.',
    },
    {
      question: 'What is the update frequency for forex quotes?',
      answer:
        'Quotes refresh at high frequency during market hours. Historical daily bars are available for every pair regardless of plan tier.',
    },
  ],
  futures: [
    {
      question: 'Which futures markets are covered?',
      answer:
        'Contracts across major exchanges are included. You can filter contract discovery by exchange, making it straightforward to build a universe for a specific market.',
    },
    {
      question: 'Do you include expired contracts?',
      answer:
        'Historical coverage focuses on active contracts, each identified by its contract specifications so continuous-style research can be assembled from individual series.',
    },
    {
      question: 'What metadata comes with each contract?',
      answer:
        'The ticker-details endpoint returns contract specifications such as exchange, product type and sizing information, alongside quotes and price history endpoints.',
    },
    {
      question: 'Is futures data suitable for systematic strategies?',
      answer:
        'Yes, clean OHLCV history per contract plus structured discovery makes it straightforward to run trend, spread or roll strategies against the data.',
    },
  ],
  indices: [
    {
      question: 'Which indices do you cover?',
      answer:
        'Benchmark indices across US and global markets are covered. The list endpoint enumerates every index, and find-tickers resolves indices by name or identifier.',
    },
    {
      question: 'Can I see the constituents of an index?',
      answer:
        'Yes, the components endpoint lists an index\'s constituent tickers, and the exposure endpoint summarises the index by factor and sector groupings.',
    },
    {
      question: 'Is index price history included?',
      answer:
        'Every index has quote and historical price endpoints, so benchmark-relative performance studies need only two calls per series.',
    },
    {
      question: 'How do indices relate to the ETFs data?',
      answer:
        'Index constituents pair naturally with ETF holdings: compare a fund\'s weights against its benchmark index directly inside your own analysis stack.',
    },
  ],
  crypto: [
    {
      question: 'Which coins and tokens are covered?',
      answer:
        'A broad universe of liquid coins and tokens is covered. Search with the find-tickers endpoint or enumerate the full list with metadata in one request.',
    },
    {
      question: 'Does crypto data include historical prices?',
      answer:
        'Yes, every supported asset carries historical OHLCV alongside live quotes and profile details, ready for backtesting 24/7 markets.',
    },
    {
      question: 'How fresh are crypto quotes?',
      answer:
        'Quotes update continuously given round-the-clock trading. Plan tiers control request rates rather than data freshness.',
    },
    {
      question: 'Can I combine crypto with traditional market data?',
      answer:
        'All datasets share the same client and response conventions, so blending crypto series with equities, macros or sentiment takes no extra plumbing.',
    },
  ],
  'corporate-profiles': [
    {
      question: 'What company information comes in a corporate profile?',
      answer:
        'Profiles cover identity and classification (sector, industry, exchange), business descriptions and key reference fields, the foundation for screening and entity resolution across every other dataset.',
    },
    {
      question: 'Is there a quick summary endpoint?',
      answer:
        'Yes, the snapshot endpoint returns an at-a-glance view combining profile, price context and headline metrics for any ticker in one call.',
    },
    {
      question: 'Do you include analyst ratings?',
      answer:
        'Street analyst ratings are part of corporate intelligence, retrievable per ticker alongside KPIs, key dates and operating information endpoints.',
    },
    {
      question: 'How often are profiles updated?',
      answer:
        'Profile and operating data refresh continuously as filings and corporate actions land, so entity records stay current without manual maintenance on your side.',
    },
  ],
  earnings: [
    {
      question: 'Do you provide earnings call transcripts?',
      answer:
        'Yes, full transcripts from earnings calls are available per ticker, ready for text analysis, sentiment scoring or feeding into retrieval pipelines.',
    },
    {
      question: 'Can I get reported results versus estimates?',
      answer:
        'The report and trend endpoints cover reported numbers alongside history and estimate trends, making surprise analysis straightforward.',
    },
    {
      question: 'How is earnings history structured?',
      answer:
        'Multi-period history per company includes the standard reporting periods so you can build consistent quarter-over-quarter and year-over-year series.',
    },
    {
      question: 'When is new earnings data available?',
      answer:
        'Results flow through close to release, and the corporate events calendar exposes upcoming earnings dates so you can schedule jobs ahead of print.',
    },
  ],
  economics: [
    {
      question: 'What kinds of economic datasets are available?',
      answer:
        'Curated macro series spanning indicators such as growth, inflation, labour and monetary measures, searchable by theme and country coverage.',
    },
    {
      question: 'How do I find a specific macro series?',
      answer:
        'The search endpoint lets you locate datasets by keyword and metadata; the data endpoint then returns observation histories aligned to your requested window.',
    },
    {
      question: 'Can economic data be joined with market data?',
      answer:
        'Yes, everything shares the same client conventions and identifiers where applicable, so aligning macro releases to asset prices is a simple merge.',
    },
    {
      question: 'Are release dates included?',
      answer:
        'Upcoming releases appear on the economic calendar endpoint, which pairs with the datasets so you can model around publication timing.',
    },
  ],
  news: [
    {
      question: 'What news sources do you cover?',
      answer:
        'The feed aggregates financial publishers, global newspapers, trade journals and regulatory newswires: hundreds of sources with millisecond-precise timestamps.',
    },
    {
      question: 'Is news tagged to companies and people?',
      answer:
        'Every article is automatically tagged with relevant companies, people, organizations and topics, so you can filter a feed down to exactly the entities in your portfolio.',
    },
    {
      question: 'Does news include sentiment scores?',
      answer:
        'Articles arrive structured with impact scoring hooks, and the sentiment section provides dedicated social, news, journalist and analyst tone scores per ticker.',
    },
    {
      question: 'Can I pull historical news for backtesting?',
      answer:
        'Yes, the archive spans years of history and is retrievable by entity, category or time window for event-study and backtest workflows.',
    },
  ],
  esg: [
    {
      question: 'What ESG data points are provided?',
      answer:
        'Environmental, Social and Governance scores for publicly listed companies, giving you a comparable sustainability signal across your universe.',
    },
    {
      question: 'How are ESG scores produced?',
      answer:
        'Scores aggregate recognised ESG assessment inputs and are normalised onto a consistent scale so cross-company comparison is meaningful.',
    },
    {
      question: 'Can ESG be combined with fundamentals?',
      answer:
        'Absolutely, scores join to tickers one-to-one, so screening on ESG thresholds alongside financial metrics is a single pipeline step.',
    },
  ],
  credit: [
    {
      question: 'Whose credit ratings do you carry?',
      answer:
        'Ratings from the major agencies are consolidated, searchable per entity, and returned with their rating reports.',
    },
    {
      question: 'How do I look up a company\'s rating?',
      answer:
        'Use the search endpoint to resolve rated entities by name, then request the report endpoint for the detailed rating record.',
    },
    {
      question: 'Is historical rating coverage available?',
      answer:
        'Rating reports include the detail needed for risk screens and fixed-income research; pair them with fundamentals for credit-adjacent equity work.',
    },
  ],
  'supply-chain': [
    {
      question: 'What relationships does the supply chain dataset capture?',
      answer:
        'Customer, supplier and peer relationships per public company: a graph you can traverse to model contagion and revenue exposure.',
    },
    {
      question: 'How far back do relationships go?',
      answer:
        'Relationships reflect disclosed connections and are refreshed as new disclosures land, supporting both point-in-time screens and longitudinal studies.',
    },
    {
      question: 'Can supply chain links improve factor models?',
      answer:
        'Yes, beta correlations along customer-supplier edges are a common use case, and the peers endpoint makes relative-value comparisons direct.',
    },
  ],
  sentiment: [
    {
      question: 'What sentiment sources do you aggregate?',
      answer:
        'Four families: social media posts, news flow, journalist tone and sell-side analyst commentary; each scored per ticker.',
    },
    {
      question: 'How are sentiment scores computed?',
      answer:
        'Source-specific signals are normalised onto comparable scales so you can test which family carries alpha for your horizon before weighting them together.',
    },
    {
      question: 'Can I build a custom blended sentiment score?',
      answer:
        'Yes, each source family is available through its own endpoint with a normalised score, so you can weight social, news and analyst signals to fit your horizon and strategy rather than relying on a fixed black-box blend.',
    },
    {
      question: 'How far back does sentiment history go?',
      answer:
        'History is retained per source so you can validate signal efficacy over long windows before deploying it live.',
    },
  ],
  webtraffic: [
    {
      question: 'What web traffic metrics do you provide?',
      answer:
        'Website engagement analytics for publicly listed companies: visit volumes and related traffic signals usable as an alternative-data input.',
    },
    {
      question: 'Why use web traffic as a market signal?',
      answer:
        'Consumer attention frequently leads fundamentals; rising engagement around a company\'s properties has documented predictive relationships that complement price-based features.',
    },
    {
      question: 'Which companies are covered?',
      answer:
        'Coverage focuses on listed companies with meaningful online presence; the API returns availability per ticker so you can screen what exists.',
    },
    {
      question: 'Can traffic data join other alternative datasets?',
      answer:
        'Yes, it shares ticker identifiers with sentiment, supply chain and fundamentals, making multi-signal feature engineering a one-line join.',
    },
  ],
  disclosures: [
    {
      question: 'What disclosure types are covered?',
      answer:
        'Regulatory disclosures filed by public companies; think insider trading forms, ownership statements and similar compliance documents; retrieved per ticker or form type.',
    },
    {
      question: 'How do disclosures differ from SEC filings?',
      answer:
        'The SEC filings section covers the broader filing corpus with search and document retrieval; disclosures focus on the compliance stream most useful for monitoring and alerting.',
    },
    {
      question: 'Can I automate monitoring on disclosures?',
      answer:
        'Yes, poll or schedule against the list endpoints to detect new items as they land, then enrich via fundamentals and insiders for context.',
    },
  ],
};
